import { supabase } from './supabase';

/**
 * Admin-only member-management helpers. Server-side enforces role checks;
 * the client-side functions here are convenience wrappers around the
 * /api/admin/members endpoints.
 */

export interface MemberRow {
  email: string;
  display_name: string | null;
  handle: string | null;
  role: 'user' | 'admin' | 'developer' | null;
  status: 'invited' | 'logged-in' | 'active' | null;
  is_founding_member: boolean;
  permissions: Record<string, boolean>;
  first_login_at: string | null;
  last_sign_in_at: string | null;
  last_invited_at: string | null;
  invite_count: number;
  user_id: string | null;
}

async function authedFetch(path: string, init?: RequestInit) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  return res;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as any)?.error ?? `Request failed (${res.status}).`;
    const detail = (data as any)?.detail;
    throw new Error(detail ? `${err}: ${detail}` : err);
  }
  return data as T;
}

export async function fetchMembers(): Promise<MemberRow[]> {
  const res = await authedFetch('/api/admin/members');
  const data = await jsonOrThrow<{ members: MemberRow[] }>(res);
  return data.members;
}

/**
 * Toggle a member's founding-member status. true grants comped Club access
 * (bypasses the paywall) + the Founding Member badge; false revokes the
 * comped access (paid subscriptions are left untouched). Server-side calls
 * set_founding_member().
 */
export async function setMemberFounding(args: {
  userId: string;
  isFounding: boolean;
}): Promise<{ user_id: string; is_founding_member: boolean }> {
  const res = await authedFetch(`/api/admin/members/${args.userId}`, {
    method: 'PUT',
    body: JSON.stringify({ is_founding_member: args.isFounding }),
  });
  return jsonOrThrow<{ user_id: string; is_founding_member: boolean }>(res);
}

/**
 * Developer-only. Set a per-admin permission flag (e.g. can_impersonate).
 * Merges into profiles.permissions. Server enforces developer role + the
 * DB guards the column.
 */
export async function setAdminPermission(args: {
  userId: string;
  key: string;
  value: boolean;
}): Promise<{ user_id: string; permissions: Record<string, boolean>; note: string }> {
  const res = await authedFetch(`/api/admin/members/${args.userId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ [args.key]: args.value }),
  });
  return jsonOrThrow<{ user_id: string; permissions: Record<string, boolean>; note: string }>(res);
}

export type MemberRole = 'user' | 'admin' | 'developer';

/**
 * Promote / demote a member. Guard rails are enforced server-side in the
 * set_member_role RPC (only developers manage the developer role, no
 * self-change, can't strip the last admin). `note` explains the JWT
 * staleness so the UI can relay it.
 */
export async function setMemberRole(args: {
  userId: string;
  role: MemberRole;
}): Promise<{ user_id: string; role: MemberRole; note: string }> {
  const res = await authedFetch(`/api/admin/members/${args.userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role: args.role }),
  });
  return jsonOrThrow<{ user_id: string; role: MemberRole; note: string }>(res);
}

/**
 * Edit a member's display name / @handle. Only the keys you pass are
 * touched. Server validates handle format + uniqueness.
 */
export async function updateMemberProfile(args: {
  userId: string;
  display_name?: string | null;
  handle?: string | null;
}): Promise<{ user_id: string; display_name?: string | null; handle?: string | null }> {
  const { userId, ...patch } = args;
  const res = await authedFetch(`/api/admin/members/${userId}/profile`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return jsonOrThrow(res);
}

/**
 * Hard-delete a member completely: their auth login + profile are destroyed
 * and their posts/uploads/comments are reassigned to an anonymous "Deleted
 * member" placeholder so threads + the feed don't break. Engagement data
 * (likes, votes, watchlist, subscription, bids) is removed. Irreversible.
 *
 * Pass reinvite to re-add the freed email to the whitelist as a fresh invite
 * (optionally as a founding member) so they can rejoin from a clean slate.
 * Server enforces: actor is admin/dev; only devs delete staff; can't delete
 * yourself or the last admin.
 */
export async function deleteMember(args: {
  userId: string;
  reinvite?: boolean;
  reinviteFounding?: boolean;
}): Promise<{ ok: true; user_id: string; email: string | null; reinvited: boolean; warn?: string }> {
  const res = await authedFetch(`/api/admin/members/${args.userId}/delete`, {
    method: 'POST',
    body: JSON.stringify({
      reinvite: !!args.reinvite,
      reinvite_founding: !!args.reinviteFounding,
    }),
  });
  return jsonOrThrow<{ ok: true; user_id: string; email: string | null; reinvited: boolean; warn?: string }>(res);
}

/** Generate a one-time magic sign-in link to hand a member. */
export async function generateSignInLink(
  userId: string,
): Promise<{ link: string; email: string }> {
  const res = await authedFetch(`/api/admin/members/${userId}/sign-in-link`, {
    method: 'POST',
  });
  return jsonOrThrow<{ link: string; email: string }>(res);
}

/**
 * Send a branded HTML invite email to a whitelisted address that hasn't
 * signed in yet. Server validates that the address is on the allowed list,
 * generates a single-use Supabase invite/magic link, sends it via Resend,
 * and stamps last_invited_at + bumps invite_count.
 */
export async function sendMemberInvite(
  email: string,
): Promise<{ ok: true; sent_to: string; invite_count: number; warn?: string }> {
  const res = await authedFetch('/api/admin/members/invite', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return jsonOrThrow<{ ok: true; sent_to: string; invite_count: number; warn?: string }>(res);
}

// ---- Bulk member email ----------------------------------------------------

export type EmailTemplate = 'invitation' | 'announcement';

export interface BulkEmailResult {
  ok: true;
  campaign_id: string | null;
  sent: number;
  skipped: Array<{ email: string; reason: string }>;
  failed: Array<{ email: string; error: string }>;
}

/**
 * Send a branded, personalized email to a list of members (or a single test to
 * yourself). The server re-validates the recipients against the whitelist,
 * skips the suppression list, generates per-recipient invite links when the
 * invite button is included, and logs every send. Pass test:true to preview to
 * your own address only.
 */
export async function sendBulkEmail(args: {
  recipients: string[];
  template: EmailTemplate;
  subject: string;
  bodyHtml: string;
  includeInviteButton: boolean;
  test?: boolean;
}): Promise<BulkEmailResult> {
  const res = await authedFetch('/api/admin/members/bulk-email', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  return jsonOrThrow<BulkEmailResult>(res);
}

export interface EmailLogRow {
  id: string;
  campaign_id: string | null;
  resend_id: string | null;
  recipient_email: string;
  template: string | null;
  subject: string | null;
  kind: string;
  status: string;
  detail: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignSummary {
  campaign_id: string;
  subject: string | null;
  template: string | null;
  created_at: string;
  total: number;
  counts: Record<string, number>;
}

/** Read the email delivery log (admin). */
export async function fetchEmailLog(params?: {
  q?: string;
  status?: string;
  limit?: number;
}): Promise<{ logs: EmailLogRow[]; campaigns: CampaignSummary[] }> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authedFetch(`/api/admin/email/logs${suffix}`);
  return jsonOrThrow<{ logs: EmailLogRow[]; campaigns: CampaignSummary[] }>(res);
}
